/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */


const styles = css`
  :host { display: inline-flex; max-width: 100%; min-width: 0; }
  .attachment { align-items: center; background: var(--card); border: 1px solid var(--border); border-radius: var(--radius-xl, 14px); color: var(--card-foreground); display: flex; flex-wrap: wrap; font-size: 0.875rem; gap: 8px; min-width: 160px; max-width: 100%; padding: 8px; position: relative; transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease; }
  .attachment:hover { background: color-mix(in oklab, var(--muted) 50%, transparent); }
  .attachment:focus-within { box-shadow: 0 0 0 1px var(--ring-shadow); }
  :host([state="idle"]) .attachment { border-style: dashed; }
  :host([state="error"]) .attachment { border-color: color-mix(in oklab, var(--destructive) 30%, transparent); }
  .media { align-items: center; aspect-ratio: 1; background: var(--muted); border-radius: var(--radius-md, 8px); display: flex; flex: 0 0 40px; justify-content: center; overflow: hidden; width: 40px; }
  :host([state="error"]) .media { background: var(--destructive-muted); color: var(--destructive); }
  .content { flex: 1; line-height: 1.25; max-width: 100%; min-width: 0; }
  .title { display: block; font-weight: 500; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  :host([state="uploading"]) .title,
  :host([state="processing"]) .title {
    animation: attachment-shimmer 1.5s linear infinite;
    background: linear-gradient(90deg, var(--card-foreground) 20%, color-mix(in oklab, var(--card-foreground) 35%, transparent) 50%, var(--card-foreground) 80%);
    background-clip: text;
    background-size: 200% 100%;
    color: transparent;
  }
  .description { color: var(--muted-foreground); display: block; font-size: 0.75rem; margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  :host([state="error"]) .description { color: color-mix(in oklab, var(--destructive) 80%, transparent); }
  .actions { align-items: center; display: flex; flex: 0 0 auto; position: relative; z-index: 2; }
  :host([size="sm"]) .attachment { font-size: 0.75rem; gap: 10px; padding: 6px; }
  :host([size="sm"]) .media { flex-basis: 32px; width: 32px; }
  :host([size="xs"]) .attachment { border-radius: var(--radius-md, 8px); font-size: 0.75rem; gap: 6px; padding: 4px; }
  :host([size="xs"]) .media { flex-basis: 28px; width: 28px; }
  :host([orientation="vertical"]) { width: 96px; }
  :host([orientation="vertical"]) .attachment { align-items: stretch; flex-direction: column; min-width: 0; width: 100%; }
  :host([orientation="vertical"]) .media { flex-basis: auto; width: 100%; }
  :host([orientation="vertical"]) .actions { position: absolute; right: 12px; top: 12px; }
  ::slotted(img[slot="media"]) { height: 100%; object-fit: cover; width: 100%; }

  @keyframes attachment-shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }

  @media (prefers-reduced-motion: reduce) {
    :host([state="uploading"]) .title,
    :host([state="processing"]) .title {
      animation: none;
      background: none;
      color: inherit;
    }
  }
`;

export class AppAttachment extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppAttachment)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["title","data-title","description","data-description"];

  #title = "";
  #description = "";
  #titleSet = false;
  #descriptionSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  get description() {
    return this.#description;
  }

  set description(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#description, next)) return;
    this.#description = next;
    this.#descriptionSet = true;
    this.#updateBindings('description');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
    if (this.hasAttribute('data-description')) this.description = this.dataset.description;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.title = props.title;
      this.description = props.description;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#titleSet) this.title = this.dataset.title;
    if (!this.#descriptionSet) this.description = this.dataset.description;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
      case 'description':
      case 'data-description':
        this.description = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="attachment"><div class="media"><slot name="media"></slot></div><div class="content"><span class="title"><!--argon-text:0-->${this.#escText(this.#title)}<!--/argon-text:0--></span><span class="description"><!--argon-text:1-->${this.#escText(this.#description)}<!--/argon-text:1--></span><slot></slot></div><div class="actions"><slot name="actions"></slot></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }


  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'title') {
      const __argonNext = `${this.#escText(this.#title)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'description') {
      const __argonNext = `${this.#escText(this.#description)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;

    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
